import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subject, Subscription } from 'rxjs';
import { ScheduleQuestionAndOptions } from '../models/Exam';
import { ExamService } from '../services/exam.service';
import { LanguageService } from '../services/language.service';
import { QuestionserviceService } from '../services/questionservice.service';

@Component({
  selector: 'app-question-group',
  templateUrl: './question-group.component.html',
  styleUrls: ['./question-group.component.css']
})
export class QuestionGroupComponent implements OnInit, OnChanges, OnDestroy {
  @Input() seletedQuestion: ScheduleQuestionAndOptions;
  @Input() examid: any;
  @Input() url: any
  @Input() adminpaused: boolean;
  @Input() proctorpaused: boolean;
  @Input() status: string;
  @Input() scheduleid: number;
  @Input() isQuestionGrouped: boolean = false;

  options: string[] = [];
  @Input() isloadingdata: boolean = false;
  @Input() requiresScreenReader: boolean;
  @Input() liteMode: boolean;
  thisurl = location.origin + '/';

  @Input() loadingSubject: Subject<boolean>;
  @Input() networkFailureSubject: Subject<boolean>;

  @Output() quesanswered: EventEmitter<number> = new EventEmitter<number>();
  @Output() quesunanswered: EventEmitter<number> = new EventEmitter<number>();
  @Output() quesflagged: EventEmitter<any> = new EventEmitter<any>();
  @Output() nextclicked: EventEmitter<number> = new EventEmitter<number>();
  @Output() previousclicked: EventEmitter<number> = new EventEmitter<number>();
  @Output() hasLastQuestionInGroup = new EventEmitter<boolean>();
  @Output() hasFirstQuestionInGroup = new EventEmitter<boolean>();
  @Output() activeQuestionChanged = new EventEmitter<number>();

  @ViewChild('questionhtmlsection') questionhtmlsection: ElementRef;
  @ViewChild('questionbody') questionbody: ElementRef;
  flagged: boolean = false;
  answersubmissioncheck: any;
  markingnotworking: boolean;
  answersubmissionservercheck: any;
  private zoomSub?: Subscription;
  private intersectionObserver?: IntersectionObserver;
  private debounceTimer?: ReturnType<typeof setTimeout>;
  private lastEmittedQuestionNo: number | null = null;
  private currentGroupId: string | null = null;

  // Debounce delay in ms — long enough to avoid rapid-fire scroll events,
  // short enough to feel responsive
  private readonly DEBOUNCE_MS = 120;

  private suppressObserver = false;
  private suppressTimer?: ReturnType<typeof setTimeout>;
  private readonly POST_CLICK_SUPPRESS_MS = 600;

  private container!: HTMLElement;

  groupedQuestions: ScheduleQuestionAndOptions[] = [];

  constructor(public questionservice: QuestionserviceService, public examservice: ExamService, public toastr: ToastrService, public router: Router, private languageService: LanguageService, private cdr: ChangeDetectorRef) { }

  get language() {
    return this.languageService.language;
  }

  get isLoadingData() {
    return this.isloadingdata;
  }

  async ngOnInit() {
    await this.loadGroupedQuestions();
    this.emitIfLastQuestionExists();
    this.emitIfFirstQuestionExists();
    
    this.container = document.getElementById('question-container') as HTMLElement;
    requestAnimationFrame(() => {
      this.scrollToSelectedQuestion();
      this.setupIntersectionObserver();
    });
  }
  
  async ngOnChanges(changes: SimpleChanges) {
    const groupChanged =
      changes['seletedQuestion'] &&
      changes['seletedQuestion'].currentValue?.examQuestion?.questionGroupID !==
        this.currentGroupId;

    if (groupChanged || !this.groupedQuestions.length) {
      await this.loadGroupedQuestions();
      this.emitIfLastQuestionExists();
      this.emitIfFirstQuestionExists();
      this.cdr.detectChanges();

      setTimeout(() => {
        this.scrollToSelectedQuestion();
        // Re-observe only when the group changes — elements may have changed
        this.setupIntersectionObserver();
      });
    } else {
      // Same group, just a different selected question — scroll only, no re-observe
      setTimeout(() => this.scrollToSelectedQuestion());
    }
  }

  ngOnDestroy() {
    this.zoomSub?.unsubscribe();
    this.teardownIntersectionObserver();
    clearTimeout(this.debounceTimer);
    clearTimeout(this.suppressTimer);
  }

  private setupIntersectionObserver() {
    this.teardownIntersectionObserver();

    if (!this.container || !this.groupedQuestions.length) return;

    // Use a low threshold array so we get callbacks at multiple visibility
    // levels, giving us a stable "most visible" winner on each scroll settle.
    this.intersectionObserver = new IntersectionObserver(
      (entries) => this.onIntersection(entries),
      {
        root: this.container,
        // Fire at 0%, 25%, 50%, 75%, 100% — enough resolution without
        // flooding the callback on every pixel of scroll.
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );

    for (const q of this.groupedQuestions) {
      const el = document.getElementById('question-' + q.examQuestion.questionid);
      if (el) this.intersectionObserver.observe(el);
    }
  }

  private teardownIntersectionObserver() {
    this.intersectionObserver?.disconnect();
    this.intersectionObserver = undefined;
  }

  /**
   * Called by IntersectionObserver. We debounce here so that rapid scroll
   * events (which produce many entries) collapse into a single emit once
   * the user stops or pauses scrolling.
   */
  private onIntersection(entries: IntersectionObserverEntry[]) {
    // Update our local ratio cache with the latest data from this batch.
    // The observer only sends *changed* entries, so we merge into a map
    // keyed by element id to always have the latest ratio for every element.
    for (const entry of entries) {
      this.visibilityRatioMap.set(entry.target.id, entry.intersectionRatio);
    }

    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.resolveActiveQuestion(), this.DEBOUNCE_MS);
  }

  // Persistent map: element id → latest intersection ratio.
  // Kept on the instance so ngOnChanges re-use doesn't lose state mid-scroll.
  private visibilityRatioMap = new Map<string, number>();

  private resolveActiveQuestion() {
    if (this.suppressObserver) return;
    let bestRatio = -1;
    let bestQuestionNo: number | null = null;
    const MIN_VISIBLE_RATIO = 0.2;

    for (const q of this.groupedQuestions) {
      const id = 'question-' + q.examQuestion.questionid;
      const ratio = this.visibilityRatioMap.get(id);
      if (!ratio) continue;

      if (ratio > MIN_VISIBLE_RATIO && ratio > bestRatio) {
        bestRatio = ratio;
        bestQuestionNo = q.examQuestion.questionno;
      }
    }

    // Only emit when the answer actually changes — prevents unnecessary
    // footer re-renders on every scroll tick.
    if (bestQuestionNo !== null && bestQuestionNo !== this.lastEmittedQuestionNo) {
      this.lastEmittedQuestionNo = bestQuestionNo;
      this.activeQuestionChanged.emit(bestQuestionNo);
    }
  }

  zoomExaminationQuestion(increase: boolean) {
    if (!this.questionbody) {
      return;
    }

    const element = this.questionbody.nativeElement;

    // Get current zoom from computed style
    const computedZoom = window.getComputedStyle(element).getPropertyValue('zoom');
    const currentZoom = parseFloat(computedZoom) || 1;

    // Adjust zoom level
    let newZoom = currentZoom + (increase ? 0.1 : -0.1);
    newZoom = Math.max(0.5, Math.min(2, newZoom)); // Clamp between 0.5 and 2
    (element.style as any).zoom = newZoom.toString();
  }


  private scrollToSelectedQuestion() {
    const target = document.getElementById('question-' + this.seletedQuestion.examQuestion.questionid);
    if (this.container && target) {
      this.container.scrollTo({
        top: target.offsetTop - this.container.offsetTop,
        behavior: 'smooth'
      });
    }
  }

  nextbtnclicked() {
    const lastQuestionInGroup = this.groupedQuestions[this.groupedQuestions.length - 1]?.examQuestion.questionno;
    this.nextclicked.emit(lastQuestionInGroup + 1);
  }

  prevbtnclicked() {
    const firstQuestionInGroup = this.groupedQuestions[0]?.examQuestion.questionno;
    this.previousclicked.emit(firstQuestionInGroup - 1);
  }

  async finishclicked() {
    if (!this.liteMode) {
      await this.questionservice.waitForAllQuestionsToBeSubmitted();
    }

    this.router.navigate(['/exam/submit'], { queryParams: { scheduleid: this.scheduleid, examid: this.examid, url: this.url } });
    localStorage.removeItem(`${this.scheduleid}_post_finish_data`);
  }

  questionAnswered(questionNo: number) {
    this.quesanswered.emit(questionNo);

  }

  questionUnanswered(questionNo: number) {
    this.quesunanswered.emit(questionNo);
  }

  flagclicked(qno: number) {
    this.quesflagged.emit({ qno, flagged:true });
  }

  getQuestionTitle(qQuestion): string {
    const prefix = qQuestion.examQuestion.questionnolabel ?
      `${qQuestion.examQuestion.questionnolabel}` :
      `${this.language.abbreviatedQuestionNo} ${qQuestion.examQuestion.questionno} of ${qQuestion.questionCount}`;

    return `${prefix} ${qQuestion.examQuestion.catergoy ? '(' + qQuestion.examQuestion.catergoy + ')' : ''}`;
  }
  
  private emitIfLastQuestionExists() {
    const exists = this.groupedQuestions?.some(
      q => q.examQuestion.questionno === this.seletedQuestion.questionCount
    );
    
    this.hasLastQuestionInGroup.emit(exists);
  }

  private emitIfFirstQuestionExists() {
    const exists = this.groupedQuestions?.some(
      q => q.examQuestion.questionno === 1
    );
    
    this.hasFirstQuestionInGroup.emit(exists);
  }

  private async loadGroupedQuestions() {
    this.currentGroupId = this.seletedQuestion.examQuestion.questionGroupID;
    this.groupedQuestions = await this.questionservice.getQuestionGroupFromStorage(
      this.currentGroupId,
      this.scheduleid,
      this.liteMode,
      this.seletedQuestion.examination.examid
    );
    // Reset ratio map when group changes so stale ids don't pollute resolution
    this.visibilityRatioMap.clear();
    this.lastEmittedQuestionNo = null;
  }
  
  onQuestionClicked(questionNo: number, questionId: number) {
    clearTimeout(this.suppressTimer);
    this.suppressObserver = true;
    this.suppressTimer = setTimeout(() => {
      this.suppressObserver = false; }, this.POST_CLICK_SUPPRESS_MS);
    
      this.lastEmittedQuestionNo = questionNo;
      this.activeQuestionChanged.emit(questionNo);
      
      const target = document.getElementById('question-' + questionId);
      
      if (this.container && target) {
        const top = target.offsetTop;
        const bottom = top + target.offsetHeight;
        const viewTop = this.container.scrollTop;
        const viewBottom = viewTop + this.container.clientHeight;
        
        const fullyInView = top >= viewTop && bottom <= viewBottom;
        
        if (!fullyInView) {
          target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }
    }
}
